<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { db } from '@/config/firebase';
import {
  getDoc,
  doc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
} from 'firebase/firestore';
import {
  format, addDays, parse, subDays,
} from 'date-fns';

import SelectableCard from '@/components/admin-dashboard/SelectableCard.vue';
import DatePicker from '@/components/admin-dashboard/DatePicker.vue';
import useToast from '@/composables/useToast';
import Toast from '@/components/Toast.vue';
// import { ChevronRightIcon } from '@vue-hero-icons/outline';

// TEMPORARY: hardcoded partnerCompanyId (later from auth user session)
const partnerCompanyId = 'ukraine-cleaning';

const service = ref<any>(null);

const selectedDate = ref('');
const unavailableSlots = ref<string[]>([]);
const loading = ref(false);
const saving = ref(false);
const showDatePicker = ref(false);

const toast = useToast();

const showToast = ref(false);

async function fetchService() {
  try {
    const servicesRef = collection(db, 'services');
    const q = query(
      servicesRef,
      where('config.partnerCompany', '==', partnerCompanyId),
    );
    const snap = await getDocs(q);

    if (!snap.empty) {
      service.value = snap.docs[0].data();
      service.value.id = snap.docs[0].id;
      // auto-set selected date based on minDaysAhead
      const minDays = service.value.slotTemplate?.minDaysAhead ?? 2;
      const defaultDate = addDays(new Date(), minDays);
      selectedDate.value = format(defaultDate, 'dd-MM-yyyy');
    }
  } catch (err) {
    console.error('Failed to fetch service', err);
  }
}

async function loadUnavailableSlots() {
  if (!service.value || !selectedDate.value) return;

  loading.value = true;
  try {
    const parsedDate = parse(selectedDate.value, 'dd-MM-yyyy', new Date());
    const stringDate = format(parsedDate, 'dd-MM-yyyy');

    const refDoc = doc(
      db,
      'partnerCompanies',
      partnerCompanyId,
      'availability',
      stringDate,
    );
    const snap = await getDoc(refDoc);

    if (snap.exists()) {
      unavailableSlots.value = (snap.data()?.unavailableSlots || []).map(
        (slot) => `${slot.start}-${slot.end}`,
      );
    } else {
      unavailableSlots.value = [];
    }
  } catch (err) {
    console.error('Failed to load slots', err);
  } finally {
    loading.value = false;
  }
}

async function saveUnavailableSlots() {
  saving.value = true;
  try {
    const parsedDate = parse(selectedDate.value, 'dd-MM-yyyy', new Date());
    const stringDate = format(parsedDate, 'dd-MM-yyyy');

    const refDoc = doc(
      db,
      'partnerCompanies',
      partnerCompanyId,
      'availability',
      stringDate,
    );

    await setDoc(refDoc, {
      serviceId: service.value.id,
      date: stringDate,
      unavailableSlots: unavailableSlots.value.map((str) => {
        const [start, end] = str.split('-');
        return { start, end };
      }),
    });
    console.log(toast);
    showToast.value = true;

    setTimeout(() => {
      showToast.value = false;
    }, 3000);
    if (!service.value || !selectedDate.value) return;
  } catch (err) {
    console.error('Save failed', err);
    alert('Failed to save availability');
  } finally {
    saving.value = false;
  }
}

function toggleSlot(slotId: string) {
  if (unavailableSlots.value.includes(slotId)) {
    unavailableSlots.value = unavailableSlots.value.filter(
      (id) => id !== slotId,
    );
  } else {
    unavailableSlots.value.push(slotId);
  }
}

function changeDay(direction: 'prev' | 'next') {
  const current = parse(selectedDate.value, 'dd-MM-yyyy', new Date());
  const newDate = direction === 'prev' ? subDays(current, 1) : addDays(current, 1);
  selectedDate.value = format(newDate, 'dd-MM-yyyy');
}

watch(selectedDate, () => {
  loadUnavailableSlots();
});

onMounted(() => {
  fetchService();
});
</script>

<template>
  <div class="p-8">
    <transition name="slide">
      <Toast
        v-if="showToast"
        variant="success"
        title="Success"
        message="Availability updated successfully"
        class="absolute top-[5%] right-[2%] z-99999"
      />
    </transition>

    <h1 class="text-2xl font-bold text-[#C59C2F] mb-6">Manage Availability - {{ service?.title }}</h1>

    <div v-if="service" class="flex items-center gap-4 mb-6">
      <button @click="changeDay('prev')" class="text-[#C59C2F]">
        <i class="pi pi-chevron-left"></i>
      </button>

      <div class="text-white font-semibold text-lg">
        {{
          format(
            parse(selectedDate, "dd-MM-yyyy", new Date()),
            "EEEE, dd MMMM yyyy"
          )
        }}
      </div>

      <button @click="changeDay('next')" class="text-[#C59C2F]">
        <!-- <Ionicons name="chevron-forward-outline" size="24" /> -->
        <i class="pi pi-chevron-right"></i>
      </button>

      <button
        @click="showDatePicker = !showDatePicker"
        class="ml-auto text-[#C59C2F]"
      >
        <i class="pi pi-calendar"></i>
      </button>
    </div>

    <div v-if="showDatePicker" class="mb-6">
      <DatePicker v-model="selectedDate" />
    </div>

    <div v-if="loading" class="text-gray-400 text-sm">Loading slots...</div>

    <div v-else-if="service" class="grid grid-cols-2 gap-4 mb-6">
      <SelectableCard
        v-for="(slot, i) in service.config.slotTemplate.slots"
        :key="i"
        :label="`${slot.start} - ${slot.end}`"
        :selected="!unavailableSlots.includes(`${slot.start}-${slot.end}`)"
        :disabled="unavailableSlots.includes(`${slot.start}-${slot.end}`)"
        @click="toggleSlot(`${slot.start}-${slot.end}`)"
      />
    </div>

    <div
      v-if="service"
      class="sticky bottom-0 bg-black py-4 flex justify-center"
    >
      <button
        @click="saveUnavailableSlots"
        class="bg-[#C59C2F] text-black font-semibold px-6 py-3 rounded-xl"
        :disabled="saving"
      >
        {{ saving ? "Saving..." : "Save Availability" }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.sticky {
  position: sticky;
}
</style>
